import { Box, Avatar, Typography, Card, CardContent, Chip, Stack, Button, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import { School, Work, AutoAwesome, Download, Handyman, RocketLaunch } from '@mui/icons-material';
import React from 'react';
import headshot from '../assets/headshot.jpeg';
import { RESUME_URL } from '../config/site';

/**
 * Fixed-width date column.
 *
 * `flex: 0.2` (the old value) is a flex *ratio*, so each row's width was still
 * derived from its own content: at phone widths "2020 - Present" / "Python ·
 * GenAI" resolved wider than "2012 - 2016" / "Java · Algorithms", which pushed
 * each row's separator to a different x and visibly staggered the connector
 * bars. A fixed width per breakpoint makes every separator share one column, so
 * the bars form one continuous line. Text wraps inside the column instead.
 */
const DateColumn = styled(TimelineOppositeContent)(({ theme }) => ({
  flex: 'none',
  width: 92,
  paddingLeft: 0,
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    width: 150,
    paddingRight: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    width: 200,
  },
}));

type Milestone = {
  period: string;
  tech: string;
  icon: React.ReactNode;
  dotColor: 'primary' | 'secondary';
  title: string;
  subtitle: string;
  body: string;
};

const milestones: Milestone[] = [
  {
    period: '2012 - 2016',
    tech: 'Java · Algorithms',
    icon: <School />,
    dotColor: 'primary',
    title: 'Duke University',
    subtitle: 'BS Computer Science',
    body: `I went to school and studied computer science in the Age Before AI (ABAI).
      It was here I discovered my love of coding. That lasted a good ten years!`,
  },
  {
    period: '2016 - 2019',
    tech: '.NET · OAuth2',
    icon: <Work />,
    dotColor: 'secondary',
    title: 'Relias',
    subtitle: 'Software Engineer · Cary, NC',
    body: `Relias was the first real job I got out of college. There, I learned how to
      think about enterprise development, and learned to use my first software frameworks
      beyond JavaFX, like Angular JS and Angular 2, .NET Core, Entity Framework, IdentityServer4,
      Ionic, and more! Building an OAuth2-based authentication and authorization server serving
      over a thousand tenants was my first big and impactful project at the company (and ever!)
      It supported the modernization of our login system and allowed us to support multiple
      company-wide microservices as Relias acquired other companies and expanded its engineering
      team count.`,
  },
  {
    period: '2019 - 2020',
    tech: 'AWS · Java',
    icon: <Work />,
    dotColor: 'primary',
    title: 'Amazon',
    subtitle: 'SDE I · Seattle, WA',
    body: `As a new hire in Amazon Customer Service Tech, I continued to learn about how to
      architect highly scalable systems. My first big challenge came in the year of COVID-19
      (2020) where all of our Customer Support Agents started working from home, leaving the
      stable Amazon Corporate Internet behind. Tens of thousands of customers were unexpectedly
      disconnecting from chats every day, and I worked on the solution to 1) quantify when and how
      this was happening with metrics, 2) design a system across multiple components to allow agents
      to easily reconnect to chats and make it less likely they would disconnect in the first place,
      and 3) if all else failed, gracefully route the customer to an agent in the same queue. I was
      an owner of all things chat, including our agent-facing frontend, our customer chat SDK, and
      the chat infrastructure that supported communication between participants, state, and
      persistence.`,
  },
  {
    period: '2020 - Present',
    tech: 'Python · GenAI',
    icon: <Work />,
    dotColor: 'primary',
    title: 'Amazon',
    subtitle: 'SDE II · Seattle, WA',
    body: `These days, I am all aboard the AI hype train. Initially, I helped deliver a full
      migration of Amazon Customer Service Chat to AWS's public CS offering, Amazon Connect,
      with traditional engineering methods (you know, like actually pointing stories, spikes,
      design review with hand written documents, meticulous coding). Afterwards, GPT 3.0 and
      ChatGPT were released; upending everything. My team transitioned to working on internal
      science team supporting projects, and I particularly built a comprehensive data labeling
      platform that helped scientists run benchmarking and evaluations at scale for their training
      jobs. That may have been the last project I truly wrote my own code for. These days, I spend
      my time using AI to help the team build effective and natural customer service experiences
      in all channels (Chat, Phone, Touch, Alexa+).`,
  },
];

type Exploration = {
  icon: React.ReactNode;
  title: string;
  body: string;
};

const explorations: Exploration[] = [
  {
    icon: <AutoAwesome color="primary" />,
    title: 'AI Gaming/Streaming Cohost',
    body: `Building an AI companion that integrates with Twitch and Kik, allowing streamers
      to have friendly (and not so friendly) banter with an AI while streaming. The AI
      reacts to events happening in Twitch, in-game, and responds to the host's dialog
      in real-time.`,
  },
  {
    icon: <RocketLaunch color="primary" />,
    title: 'A Company of One',
    body: `A secret side quest: testing whether one person can build an entire company end to
      end (product, backend, frontend, infra, design, and copy) with AI covering everything
      that used to require hiring for it. Ideas are the currency in this day and age, and
      execution has never been cheaper. Details stay vague for now.`,
  },
  {
    icon: <Handyman color="primary" />,
    title: 'Home Improvement',
    body: `My oven decided its buttons work better without me: they press themselves, I can't
      press them, and the net result is an oven that turns itself on. Somewhere in there is a
      membrane switch and a control board, and I intend to find out which before paying a $120
      diagnostic fee for someone to tell me. Also open: a door hinge whose screw hole hollowed
      out so the screws just spin, and a sliding screen door I have now failed to correctly
      measure and assemble exactly once. Physical systems have much worse error messages than
      software.`,
  },
];

const About: React.FC = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: 4,
          mb: 6,
        }}
      >
        <Avatar
          alt="Leo Feldman"
          src={headshot}
          sx={{
            width: { xs: 150, md: 200 },
            height: { xs: 150, md: 200 },
            boxShadow: 3,
          }}
        />
        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Leo Feldman
          </Typography>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Software Development Engineer @ Amazon
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Seattle, WA
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, maxWidth: 600 }}>
            10 years building web, backend, and AI systems. Currently exploring the power of
            generative AI to automate Amazon Customer Service at scale.
          </Typography>
          <Button
            component="a"
            href={RESUME_URL}
            variant="contained"
            color="primary"
            size="large"
            startIcon={<Download />}
            sx={{ mt: 3 }}
          >
            Download Resume
          </Button>
        </Box>
      </Box>

      {/* Journey Timeline */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        Journey
      </Typography>
      <Card sx={{ mb: 6 }}>
        <CardContent>
          <Timeline position="right" sx={{ px: { xs: 0, sm: 2 } }}>
            {milestones.map((m, i) => (
              <TimelineItem key={m.period}>
                <DateColumn>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {m.period}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {m.tech}
                  </Typography>
                </DateColumn>
                <TimelineSeparator>
                  <TimelineDot color={m.dotColor}>{m.icon}</TimelineDot>
                  {i < milestones.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="h6">{m.title}</Typography>
                  <Typography color="text.secondary">{m.subtitle}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {m.body}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </CardContent>
      </Card>

      {/* Currently Exploring */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        Currently Exploring
      </Typography>
      <Grid container spacing={2} sx={{ mb: 6 }}>
        {explorations.map((e) => (
          <Grid size={{ xs: 12, md: 4 }} key={e.title}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  {e.icon}
                  <Typography variant="h6">{e.title}</Typography>
                </Stack>
                <Typography variant="body1">{e.body}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Skills */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        Skills
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 6 }} useFlexGap>
        <Chip label="Java" />
        <Chip label="Python" />
        <Chip label=".NET" />
        <Chip label="React" />
        <Chip label="Angular" />
        <Chip label="TypeScript" />
        <Chip label="AWS Lambda" />
        <Chip label="ECS Fargate" />
        <Chip label="LLM Pipelines" />
        <Chip label="OAuth2" />
      </Stack>

      {/* Outside Work */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        Outside Work
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Living in Seattle with my wife Bahnny and our two cats, Mei and Kali.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
        <Chip label="Climbing @ Seattle Bouldering Project" variant="outlined" />
        <Chip label="Biking to Work" variant="outlined" />
        <Chip label="Story-Rich Video Games" variant="outlined" />
        <Chip label="Building Software" variant="outlined" />
        <Chip label="Scuba Diving" variant="outlined" />
      </Stack>
    </Box>
  );
};

export default About;
